import { NgModule, Provider } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Routes, RouterModule } from '@angular/router';

import { FlexLayoutModule } from '@angular/flex-layout';

import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSliderModule } from '@angular/material/slider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTableModule } from '@angular/material/table';
import { MatStepperModule } from '@angular/material/stepper';

// models
import { Project } from '@app/shared/models/project';
import { Job } from '@app/shared/models/job';
import { User } from '@app/shared/models/user';
import { UserPrefs } from '@app/shared/models/userPrefs';
import { Offer } from '@app/shared/models/offer';
import { OfferActivity } from '@app/shared/models/offerActivity';
import { OfferType } from '@app/shared/models/offerType';
import { OfferAsset } from '@app/shared/models/offerAsset';
import { Product } from '@app/shared/models/product';
import { ProductLineItem } from '@app/shared/models/productLineItem';
import { PublicOfferExtraInfos } from '@app/shared/models/publicOfferExtraInfos';
import { Region } from '@app/shared/models/region';
import { Disclaimer } from '@app/shared/models/disclaimer';
import { DocumentSegment } from '@app/shared/models/documentSegment';
import { Team } from '@app/shared/models/team';
import { Page } from '@app/shared/models/page';
import { PageLayout } from '@app/shared/models/pageLayout';
import { MasterRegionTemplate } from '@app/shared/models/masterRegionTemplate';
import { PageBackground } from '@app/shared/models/pageBackground';
import { OfferAllocation } from '@app/shared/models/offerAllocation';
import { ValidationError } from '@app/shared/models/validationError';
import { Error } from '@app/shared/models/error';
import { DashboardData } from '@app/shared/models/dashboardData';
import { Notification } from '@app/shared/models/notification';
import { Decoration } from '@app/shared/models/decoration';
import { PublicOfferAsset } from '@app/shared/models/publicOfferAsset';
import { Report } from '@app/shared/models/report';
import { Studio } from '@app/shared/models/studio';

import { Icon } from '@app/shared/models/icon';
import { Media } from '@app/shared/models/media';
import { PricePointTemplate } from '@app/shared/models/pricepointTemplate';
import { OfferTemplate } from '@app/shared/models/offerTemplate';
import { PageTemplate } from '@app/shared/models/pageTemplate';
import { Symbol } from '@app/shared/models/symbol';
import { TextSymbol } from '@app/shared/models/textSymbol';
import { IStudioLibraryItem } from '@app/shared/models/IStudioLibraryItem';
import { ILiveArea } from '@app/shared/models/ILiveArea';
import { StudioTemplate } from '@app/shared/models/studioTemplate';
import { AssetType } from '@app/shared/models/assetType';
import { RequestStatus } from '@app/shared/models/requestStatus';

// pipes
import { DateTimeFormatPipe } from '@app/shared/utils/dateTimeFormatPipe';
import { DateFormatPipe } from '@app/shared/utils/dateFormatPipe';
import { UniquePipe, ClassPipe, AttributePipe, FilterPipe, SortyByPipe } from '@app/shared/utils/uniquePipe';
import { StudioUtils } from '@app/shared/utils/studio-utils';
import { JobUtils } from '@app/shared/utils/job-utils';

// components
import { OfferEditorComponent } from './components/offer-editor/offer-editor.component';
import { FavouriteListComponent } from './components/offer-editor/favourite-list/favourite-list.component';
import { AssetSearchComponent } from './components/offer-editor/asset-search/asset-search.component';
import { ProductSearchComponent } from './components/offer-editor/product-search/product-search.component';
import { OfferSearchComponent } from './components/offer-editor/offer-search/offer-search.component';
import { VendorUploadComponent } from './components/offer-editor/vendor-upload/vendor-upload.component';
import { CopyDialogComponent } from './components/copy-dialog/copy-dialog.component';
import { ReportDialogComponent } from './components/report-dialog/report-dialog.component';
import { SendToStudioDialogComponent } from './components/send-to-studio-dialog/send-to-studio-dialog.component';
import { ApplyBackgroundDialogComponent } from './components/apply-background-dialog/apply-background-dialog.component';


import { InlineEditComponent } from './components/inline-edit/inline-edit.component';
import { CopyOfferDialogComponent } from './components/copy-offer-dialog/copy-offer-dialog.component';
import { UploadDialogComponent } from './components/upload-dialog/upload-dialog.component';
import { OfferHistoryDialogComponent } from './components/offer-history-dialog/offer-history-dialog.component';
import { AllocationEditorComponent } from './components/allocation-editor/allocation-editor.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';

import { LoaderComponent } from './components/loader/loader.component';
import { HeaderLoaderComponent } from './components/loader/header-loader.component';
import { MulitplaneGridDirective } from './directives/multiplane-grid.directive';
import { NumberOnlyDirective } from './directives/number-only.directive';
import { RestrictInputDirective } from './directives/restrict-input.directive';
import { DataMetaDisplayDirective } from './directives/data-meta-display.directive';
import { MaskedElementDirective } from './directives/masked-element.directive';
import { SelectableElementDirective } from './directives/selectable-element.directive';
import { TemplateHolderDirective } from './directives/template-holder.directive';
import { CanAccessDirective } from './directives/can-access.directive';
import { DelayedRendererDirective } from './directives/delayed-renderer.directive';
import { PermissionMenuDirective } from './directives/permission-menu.directive';
import { PopoverDirective } from './directives/popover.directive';

import { ModalStatusIconComponent } from './components/modal-status-icon/modal-status-icon.component';
import { OfferFaveComponent } from './components/offer-fave/offer-fave.component';
import { NotificationComponent } from './components/notification/notification.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
//import { NavbarComponent } from './components/offer-editor/offer-search/offer-search.component';


// 3rd Party
import { Ng2PageScrollModule } from 'ng2-page-scroll';
import { InViewportModule, WindowRef } from '@thisissoon/angular-inviewport';
import { ScrollSpyModule } from '@thisissoon/angular-scrollspy';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

import { NgDragDropModule } from 'ng-drag-drop';
import { OrderModule } from 'ngx-order-pipe';
// import { NgStickyDirective } from 'ng-sticky';
import { NgStickyModule } from 'ng-sticky';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { GridsterModule } from 'angular-gridster2';
import { NguCarouselModule } from '@ngu/carousel';
import { ImgFallbackModule } from 'ngx-img-fallback';
import { Ng2PanZoomModule } from 'ng2-panzoom';
import { NgProgressModule } from '@ngx-progressbar/core';
import { ColorPickerModule } from 'ngx-color-picker';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { NgxImageGalleryModule } from 'ngx-image-gallery';

import { environment } from 'environments/environment';
import { AvatarComponent } from './components/avatar/avatar.component';


// // moment
// import {MomentDateAdapter} from '@angular/material-moment-adapter';
// import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';

// Provide window object for browser and a suitable replacement
// on other platforms
export const getWindow = () => window;

export const inViewportProviders: Provider[] = [
  { provide: WindowRef, useFactory: (getWindow) }
];

// scroll configs
const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true
};

@NgModule({
  imports: [

    CommonModule,
    RouterModule,
    InViewportModule.forRoot(inViewportProviders),
    ScrollSpyModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,

    MatButtonModule,
    MatToolbarModule,
    MatSelectModule,
    MatTabsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatChipsModule,
    MatCardModule,
    MatSidenavModule,
    MatCheckboxModule,
    MatListModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDividerModule,
    MatButtonToggleModule,
    MatExpansionModule,
    MatDialogModule,
    MatGridListModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatTableModule,
    MatStepperModule,

    Ng2PageScrollModule,
    MatSliderModule,
    PerfectScrollbarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgDragDropModule.forRoot(),
    OrderModule,
    NgxDatatableModule,
    GridsterModule,
    NguCarouselModule,
    ImgFallbackModule,
    Ng2PanZoomModule,
    NgProgressModule.forRoot(),
    ColorPickerModule,
    NgxImageGalleryModule,
    NgStickyModule,
    LoggerModule.forRoot({level: NgxLoggerLevel.INFO})
  ],
  declarations: [
    OfferEditorComponent,
    FavouriteListComponent,
    AssetSearchComponent,
    ProductSearchComponent,
    OfferSearchComponent,
    LoaderComponent,
    HeaderLoaderComponent,
    MulitplaneGridDirective,
    DateTimeFormatPipe,
    DateFormatPipe,
    UniquePipe,
    ClassPipe,
    AttributePipe,
    FilterPipe,
    SortyByPipe,
    ModalStatusIconComponent,
    CopyDialogComponent,
    ReportDialogComponent,
    SendToStudioDialogComponent,
    ApplyBackgroundDialogComponent,
    UploadDialogComponent,
    CopyOfferDialogComponent,
    OfferHistoryDialogComponent,
    AllocationEditorComponent,
    InlineEditComponent,
    // NgStickyDirective,
    NumberOnlyDirective,
    RestrictInputDirective,
    DataMetaDisplayDirective,
    MaskedElementDirective,
    SelectableElementDirective,
    TemplateHolderDirective,
    CanAccessDirective,
    DelayedRendererDirective,
    PermissionMenuDirective,
    PopoverDirective,
    OfferFaveComponent,
    FavouriteListComponent,
    NotificationComponent,
    NavbarComponent,
    AllocationEditorComponent,
    ErrorPageComponent,
    VendorUploadComponent,
    AvatarComponent,
    ConfirmationDialogComponent,
  ],
  providers:[StudioUtils,
    DateTimeFormatPipe,
    DateFormatPipe,
    JobUtils,
    DatePipe,
    {
        provide: PERFECT_SCROLLBAR_CONFIG,
        useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
    },


        // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
        // application's root module. We provide it at the component level here, due to limitations of
        // our example generation script.
        // {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
        //
        // {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
  ],
  exports: [
    CommonModule,
    RouterModule,
    InViewportModule,
    ScrollSpyModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    MatButtonModule,
    MatMenuModule,
    MatTabsModule,
    MatChipsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatCheckboxModule,
    MatCardModule,
    MatSidenavModule,
    MatListModule,
    MatSelectModule,
    MatToolbarModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDividerModule,
    MatButtonToggleModule,
    MatExpansionModule,
    MatDialogModule,
    Ng2PageScrollModule,
    MatSliderModule,
    PerfectScrollbarModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatGridListModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatTableModule,
    MatStepperModule,
    NgDragDropModule,
    OrderModule,
    NgxDatatableModule,
    NgProgressModule,
    ColorPickerModule,
    LoggerModule,

    OfferEditorComponent,
    LoaderComponent,
    HeaderLoaderComponent,
    MulitplaneGridDirective,
    DateTimeFormatPipe,
    DateFormatPipe,
    UniquePipe,
    ClassPipe,
    AttributePipe,
    FilterPipe,
    SortyByPipe,
    ModalStatusIconComponent,
    CopyDialogComponent,
    ReportDialogComponent,
    SendToStudioDialogComponent,
    ApplyBackgroundDialogComponent,
    CopyOfferDialogComponent,
    UploadDialogComponent,
    OfferHistoryDialogComponent,
    AllocationEditorComponent,
    InlineEditComponent,
    AvatarComponent,
    ConfirmationDialogComponent,
    // NgStickyDirective,
    NumberOnlyDirective,
    RestrictInputDirective,
    DataMetaDisplayDirective,
    MaskedElementDirective,
    SelectableElementDirective,
    TemplateHolderDirective,
    CanAccessDirective,
    DelayedRendererDirective,
    PermissionMenuDirective,
    PopoverDirective,
    ErrorPageComponent,

    GridsterModule,
    NguCarouselModule,
    ImgFallbackModule,
    OfferFaveComponent,
    FavouriteListComponent,
    Ng2PanZoomModule,
    NotificationComponent,
    NavbarComponent,
    NgxImageGalleryModule,
    NgStickyModule
  ],
  entryComponents: [OfferEditorComponent,CopyDialogComponent,ReportDialogComponent,SendToStudioDialogComponent,ApplyBackgroundDialogComponent,CopyOfferDialogComponent,OfferHistoryDialogComponent,UploadDialogComponent,AllocationEditorComponent,ConfirmationDialogComponent]
})
export class SharedModule {}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/pmclient-uat/src/app/shared/shared.module.ts